import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Grid, Select, Input, Card, Typography, Spin, Tabs, Empty } from "@arco-design/web-react";
import axios from "axios";

import { trackEvent } from "../analytics/track";
import { endpoints } from "../api/endpoints";
import { User } from "../models/Person";
import useFetchSearchFilters from "../api/hooks/useFetchSearchFilters";
import { PersonCard } from "../components/PersonCard";
import { useEventIntakeEnabled } from "../hooks/useEventIntakeEnabled";
import {
  filterBrowsePeopleResults,
  BrowsePeopleRole,
} from "../utils/filterBrowsePeopleByRole";
import { RootState } from "../store/store";
import "../components/PillTabs/pill-tabs.css";
import "./Search.css";

const Option = Select.Option;
const InputSearch = Input.Search;
const Row = Grid.Row;
const Col = Grid.Col;
const TabPane = Tabs.TabPane;

export type { BrowsePeopleRole };

export const Search = () => {
  const { t } = useTranslation();
  const eventIntakeEnabled = useEventIntakeEnabled();
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);
  const { categories, industries, isLoading } = useFetchSearchFilters();

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");
  const [role, setRole] = useState<BrowsePeopleRole>("all");
  const isFirstSearchRef = useRef(true);

  const [profiles, setProfiles] = useState<User[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearchQuery(searchInput), 400);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const getProfiles = useCallback(async () => {
    const roleParam = eventIntakeEnabled ? "all" : "mentors";
    setProfilesLoading(true);
    axios
      .get(
        `${endpoints.getSearchMentors}&q=${encodeURIComponent(searchQuery || "")}&category=${encodeURIComponent(category || "")}&industry=${encodeURIComponent(industry || "")}&role=${encodeURIComponent(roleParam)}`,
      )
      .then((response) => {
        const results = (response.data.mentors ?? []) as User[];
        const nextProfiles = eventIntakeEnabled
          ? filterBrowsePeopleResults(results, role, useNycIntake)
          : results;
        setProfiles(nextProfiles);
        const isInitialEmpty =
          !searchQuery && !category && !industry && role === "all";
        if (!(isFirstSearchRef.current && isInitialEmpty)) {
          trackEvent("browse_search_action", {
            q: searchQuery || "all",
            category: category || "all",
            industry: industry || "all",
            role: eventIntakeEnabled ? role : "mentors",
            result_count: nextProfiles.length,
          });
        }
        isFirstSearchRef.current = false;
      })
      .catch((error) => {
        console.error(error);
        setProfiles([]);
        isFirstSearchRef.current = false;
      })
      .finally(() => {
        setProfilesLoading(false);
      });
  }, [category, eventIntakeEnabled, industry, role, searchQuery, useNycIntake]);

  useEffect(() => {
    getProfiles();
  }, [getProfiles]);

  useEffect(() => {
    if (useNycIntake && role === "mentees") {
      setRole("all");
    }
  }, [role, useNycIntake]);

  const pageTitle = eventIntakeEnabled
    ? t("find_mentors_page.browse_people")
    : t("find_mentors_page.search_mentors");

  return isLoading ? (
    <Spin style={{ margin: "120px auto" }} size={32} />
  ) : (
    <Card className="card-with-icon-hover own-card">
      <Typography.Title
        heading={3}
        style={{
          textAlign: "left",
          margin: "10px 0 20px 0",
          wordBreak: "break-word",
          textTransform: "uppercase",
        }}
      >
        {pageTitle}
      </Typography.Title>

      {eventIntakeEnabled ? (
        <Tabs
          activeTab={role}
          onChange={(key) => setRole(key as BrowsePeopleRole)}
          type="rounded"
          className="search-page__tabs pill-tabs"
        >
          <TabPane key="all" title={t("find_mentors_page.tab_all")} />
          <TabPane key="mentors" title={t("find_mentors_page.tab_mentors")} />
          {!useNycIntake ? (
            <TabPane key="mentees" title={t("find_mentors_page.tab_mentees")} />
          ) : null}
          <TabPane key="guests" title={t("find_mentors_page.tab_guests")} />
        </Tabs>
      ) : null}

      <Row
        justify="space-between"
        style={{ marginBottom: 20, marginLeft: 10, marginRight: 10 }}
        gutter={20}
      >
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <InputSearch
            onChange={(e) => setSearchInput(e)}
            placeholder={t("find_mentors_page.search_placeholder")}
            onPressEnter={() => setSearchQuery(searchInput)}
            style={{ marginBottom: 10 }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <Select
            placeholder={t("find_mentors_page.category_placeholder")}
            allowClear
            onChange={(value) => setCategory(value)}
            showSearch={true}
            style={{ marginBottom: 10 }}
          >
            {categories.map((option) => (
              <Option key={`Category-${option.label}`} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <Select
            placeholder={t("find_mentors_page.industry_placeholder")}
            allowClear
            onChange={(value) => setIndustry(value)}
            showSearch={true}
          >
            {industries.map((option) => (
              <Option key={`Industry-${option.label}`} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      {profilesLoading ? (
        <Spin style={{ margin: "48px auto", display: "block" }} size={32} />
      ) : profiles.length === 0 ? (
        <Empty
          className="search-page__empty"
          description={t("find_mentors_page.no_results")}
        />
      ) : (
        <Row gutter={20}>
          {profiles.map((profile) => (
            <Col
              key={profile.id}
              span={24}
              xl={{ span: 8 }}
              xs={{ span: 24 }}
              md={{ span: 12 }}
              style={{ marginBottom: 20 }}
            >
              <PersonCard person={profile} />
            </Col>
          ))}
        </Row>
      )}
    </Card>
  );
};
